import { translate } from '@/i18n';
import { TournamentSystem, TournamentSystemLabel, ControlType, TournamentStatus, ChessVariant, CompetitionFormat } from '@/services/utils/enums';
import { formatPublicSearchResult, PublicSearchResultType, buildPublicSearchResponse } from '@/services/publicSearchService';

function parseDay(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : new Date(date.getFullYear(), date.getMonth(), date.getDate());
}
const normalizeSearch = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
function paginate(items, page, pageSize) {
  const total = items.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  page = Math.min(Math.max(1, Number(page || 1)), pageCount);
  return { data: items.slice((page - 1) * pageSize, page * pageSize), meta: { pagination: { page, pageSize, pageCount, total } } };
}
async function getMockSearchItems() {
  const [{ mockClubs }, { homepageMockData }, { mockTournaments }] = await Promise.all([
    import('@/mock/entities/clubs'),
    import('@/mock/HomePage/homepageMockData'),
    import('@/mock/entities/tournaments'),
  ]);
  const events = [...mockTournaments]
    .sort((first, second) => parsePublicSearchDate(second.date) - parsePublicSearchDate(first.date))
    .map((event) => formatPublicSearchResult(PublicSearchResultType.EVENT, event));

  const clubs = mockClubs.map((club) => formatPublicSearchResult(PublicSearchResultType.CLUB, club));

  const cities = homepageMockData.cities.map((city) => formatPublicSearchResult(PublicSearchResultType.CITY, city));

  return [...events, ...clubs, ...cities];
}

function parsePublicSearchDate(value) {
  if (!value || typeof value !== 'string') return 0;
  const parts = value.split('.').map((part) => Number(part));
  if (parts.length !== 3 || parts.some((part) => !Number.isFinite(part))) return 0;
  const [day, month, year] = parts;
  return new Date(year, month - 1, day).getTime();
}

async function filterMockSearchItems(query = '', activeTypes = null) {
  const normalizedQuery = query.trim().toLowerCase();
  const shouldFilterByType = Array.isArray(activeTypes);
  const enabledTypes = new Set(activeTypes || []);

  return (await getMockSearchItems()).filter((item) => {
    const matchesType = !shouldFilterByType || enabledTypes.has(item.type);
    const matchesQuery = !normalizedQuery || item.searchText.toLowerCase().includes(normalizedQuery);

    return matchesType && matchesQuery;
  });
}


const MockService = {
  async getClubBySlug(slug) {
    const { mockClubs } = await import('@/mock/entities/clubs');
    const club = mockClubs.find(item => item.slug === slug);
    return club ? { ...club } : null;
  },
  async loadClubHome(club, locale) {
    const { createClubHomeMockData } = await import('@/mock/ClubHome/clubHomeMockData');
    return createClubHomeMockData(club, locale);
  },
  async loadHomepageTournaments(limit) {
    const { mockTournaments } = await import('@/mock/entities/tournaments');
    return mockTournaments.slice(0, limit);
  },
  async loadHomepageClubs(limit) {
    const { mockClubs } = await import('@/mock/entities/clubs');
    return mockClubs.slice(0, limit);
  },
  async loadHomepageResults() {
    const { homepageMockData } = await import('@/mock/HomePage/homepageMockData');
    return { results: homepageMockData.results, movers: homepageMockData.movers, performers: homepageMockData.performers };
  },
  async loadHomepageStats() {
    const { homepageStats } = await import('@/mock/public/publicStats');
    return homepageStats;
  },
  async loadPlayerStats() {
    const { playerStats } = await import('@/mock/public/publicStats');
    return playerStats;
  },
  async search(query = '', types = null, options = {}) {
    return buildPublicSearchResponse(await filterMockSearchItems(query, types), options);
  },
  async loadTournaments({ page = 1, pageSize = 12, clubId, ...options } = {}) {
    const { mockTournaments } = await import('@/mock/entities/tournaments');
    if (clubId === 'mock-new-city') return paginate([], page, pageSize);
    const normalizedSearch = String(options.searchQuery || '').toLowerCase();
    const fromDay = parseDay(options.dateFrom);
    const toDay = parseDay(options.dateTo);

    const filtered = mockTournaments
      .map((tournament, index) => this.normalizeMockTournament(tournament, index))
      .filter((tournament) => {
        const tournamentDay = parseDay(tournament.date);
        const matchesSearch = !normalizedSearch
          || [
            tournament.name,
            tournament.system,
            TournamentSystemLabel[tournament.system],
            tournament.control,
          ].filter(Boolean).join(' ').toLowerCase().includes(normalizedSearch);
        const matchesDateFrom = !fromDay || (tournamentDay && tournamentDay >= fromDay);
        const matchesDateTo = !toDay || (tournamentDay && tournamentDay <= toDay);
        const matchesStatus = !options.status || tournament.status === options.status;
        const matchesRated = options.rated == null || tournament.rated === options.rated;
        const matchesChessVariant = !options.chessVariant || tournament.chessVariant === options.chessVariant;
        const matchesSystem = !options.system || tournament.system === options.system;
        const matchesControlType = !options.controlType || tournament.controlType === options.controlType;
        const matchesSeason = options.withSeason
          ? Boolean(tournament.season) && (!options.seasonId || tournament.season?.id === options.seasonId)
          : options.withoutSeason
            ? !tournament.season
            : true;

        return matchesSearch
          && matchesDateFrom
          && matchesDateTo
          && matchesStatus
          && matchesRated
          && matchesChessVariant
          && matchesSystem
          && matchesControlType
          && matchesSeason;
      })
      .sort((a, b) => {
        const aTime = parseDay(a.date)?.getTime() || 0;
        const bTime = parseDay(b.date)?.getTime() || 0;
        return bTime - aTime;
      });
    return paginate(filtered, page, pageSize);
  },
  normalizeMockTournament(tournament, index = 0) {
    const system = tournament.systemType || tournament.system || TournamentSystem.SWISS;
    const controlType = tournament.controlType || ControlType.BLITZ;

    return {
      id: `mock-tournament-${index + 1}`,
      slug: `mock-tournament-${index + 1}`,
      name: tournament.title || tournament.name || translate('tournamentList.defaultName'),
      date: this.normalizeMockDate(tournament.date),
      status: TournamentStatus.CREATED,
      system,
      rated: index % 2 === 0,
      chessVariant: ChessVariant.CLASSIC,
      competitionFormat: CompetitionFormat.INDIVIDUAL,
      control: tournament.control || '',
      controlType,
      playersCount: Number(String(tournament.players || '').split('/')[0].trim()) || 24,
      gamesCount: Number(tournament.gamesCount || 0),
      players: [],
      games: { regular: [], elimination: { top: [], bottom: [] }, other: [] },
      swiss: system === TournamentSystem.SWISS
        ? { rounds: 7, timeMinutes: controlType === ControlType.RAPID ? 7 : 3, increment: controlType === ControlType.RAPID ? 5 : 2, controlType }
        : null,
      roundRobin: system === TournamentSystem.ROUND_ROBIN
        ? { cycles: 1, timeMinutes: 7, increment: 5, controlType }
        : null,
      season: null,
    };
  },
  normalizeMockDate(value) {
    if (!value || typeof value !== 'string') return new Date().toISOString();
    const match = value.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (!match) return value;
    return `${match[3]}-${match[2]}-${match[1]}T12:00:00.000Z`;
  },
  async loadSeasons({ page = 1, pageSize = 12, clubId, ...options } = {}) {
    const { mockSeasons } = await import('@/mock/entities/seasons');
    if (clubId === 'mock-new-city') return paginate([], page, pageSize);
    const normalizedSearch = String(options.searchQuery || '').toLowerCase();
    const startsFromDay = parseDay(options.startsFrom);
    const startsToDay = parseDay(options.startsTo);
    const endsFromDay = parseDay(options.endsFrom);
    const endsToDay = parseDay(options.endsTo);

    const sorted = [...mockSeasons]
      .filter((season) => {
        const startsAtDay = parseDay(season.startsAt);
        const endsAtDay = parseDay(season.endsAt);
        const matchesSearch = !normalizedSearch
          || [
            season.name,
            season.description,
          ].filter(Boolean).join(' ').toLowerCase().includes(normalizedSearch);
        const matchesStatus = !options.status || season.status === options.status;
        const matchesStartsFrom = !startsFromDay || (startsAtDay && startsAtDay >= startsFromDay);
        const matchesStartsTo = !startsToDay || (startsAtDay && startsAtDay <= startsToDay);
        const matchesEndsFrom = !endsFromDay || (endsAtDay && endsAtDay >= endsFromDay);
        const matchesEndsTo = !endsToDay || (endsAtDay && endsAtDay <= endsToDay);

        return matchesSearch
          && matchesStatus
          && matchesStartsFrom
          && matchesStartsTo
          && matchesEndsFrom
          && matchesEndsTo;
      })
      .sort((a, b) => {
      const aTime = new Date(a.startsAt || a.createdAt || 0).getTime();
      const bTime = new Date(b.startsAt || b.createdAt || 0).getTime();
      return bTime - aTime;
    });
    return paginate(sorted, page, pageSize);
  },
  async loadClubs({ page = 1, pageSize = 12, searchQuery = '', ownClubId, ownClubSlug } = {}) {
    const { mockClubs } = await import('@/mock/entities/clubs');
    const query = normalizeSearch(searchQuery);
    const filtered = mockClubs
      .filter((club) => {
        const matchesQuery = !query || normalizeSearch([club.name, club.city, club.country, club.slug].filter(Boolean).join(' ')).includes(query);

        return matchesQuery;
      })
      .sort((a, b) => {
        const aIsOwn = (a.id === ownClubId || a.slug === ownClubSlug) ? 1 : 0;
        const bIsOwn = (b.id === ownClubId || b.slug === ownClubSlug) ? 1 : 0;
        if (aIsOwn !== bIsOwn) return bIsOwn - aIsOwn;
        return String(a.name || '').localeCompare(String(b.name || ''), 'ru');
      });
    return paginate(filtered, page, pageSize);
  },


};
export default MockService;
